import React, { useState, useEffect } from "react";
import { Box, TextField, FormControl, Typography, MenuItem, Select, InputLabel } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../components/Header";
import { useAlert } from "../../../global/AlertContext";
import { useNavigate, useParams } from "react-router-dom";
import { useGetProductByIdQuery, useUpdateProductMutation } from "../../../services/productService";
import { product_types, product_UOM } from "../../../constants/enums";
import ErrorComponent from "../../../components/ErrorComponent";
import LoadingComponent from "../../../components/LoadingComponent";
import SuccessButton from "../../../components/SuccessButton";
import CancelButton from "../../../components/CancelButton";

const EditProduct = () => {
  const showAlert = useAlert();
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: product, isLoading, error, refetch } = useGetProductByIdQuery(id);
  const [updateProduct] = useUpdateProductMutation();

  const [initialValues, setInitialValues] = useState({
    name: "",
    UOM: "",
    type: ""
  });

  useEffect(() => {
    if (product) {
      setInitialValues({
        name: product.name,
        UOM: product.UOM,
        type: product.type
      });
    }
  }, [product]);

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      await updateProduct({ id, updatedProduct: values }).unwrap();
      await refetch();
      showAlert("Producto actualizado!", "success");
      navigate('/products'); 
    } catch (error) {
      console.error("Error updating product:", error);
      showAlert(error.message, "error");
    }
  };

  const productSchema = yup.object().shape({
    name: yup.string().required("Requerido"),
    UOM: yup.string().required("Requerido"),
    type: yup.string().required("Requerido")
  });

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <Box m="20px">
      <Header title="Editar Producto" subtitle="Editar detalles del producto" />
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={productSchema}
        enableReinitialize
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Nombre del Producto"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name}
                name="name"
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 2" }}
              />
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel id="UOM-label">Unidad de medida</InputLabel>
                <Select
                  labelId="UOM-label"
                  id="UOM"
                  name="UOM"
                  value={values.UOM}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.UOM && !!errors.UOM}
                >
                  {product_UOM.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
                {touched.UOM && errors.UOM && (
                  <Typography variant="caption" color="error">
                    {errors.UOM}
                  </Typography>
                )}
              </FormControl>
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel id="type-label">Tipo</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.type && !!errors.type}
                >
                  {product_types.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
                {touched.type && errors.type && (
                  <Typography variant="caption" color="error">
                    {errors.type}
                  </Typography>
                )}
              </FormControl>
            </Box>

            <Box
              display="flex"
              justifyContent="end"
              textAlign="center"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/products')}/>
              <SuccessButton label="Actualizar Producto" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditProduct;